import { MetadataJsonSchema, type MetadataJson } from './metadata-json.js';
import { z } from 'zod';
import type { NormalizedRide } from './contracts.js';
import type { RenderOutput } from './presets.js';
import { resolveExportTiming } from './export-range.js';
import { availableVideoDownloadFilename, videoDownloadFilename } from './download-filename.js';

const text = (maximum: number) => z.string().max(maximum).refine(value => !value.includes('\0'));
const instant = z.iso.datetime({ offset: true });
/** Only this bounded, versioned document is passed to FFmpeg. */
export const ExportMetadataSchema = z.object({
  schemaVersion: z.literal(1), product: z.literal('OVERLAND TRACES'), website: z.literal('https://overlandtraces.com'),
  exportId: text(128).min(1), createdAt: instant, filename: text(240).min(1), title: text(160).min(1),
  accountUsername: text(256).nullable(), creator: text(256).nullable(), sourceFileName: text(255).nullable(),
  format: z.object({ aspect: z.enum(['16x9', '4x5', '9x16']), width: z.int().positive(), height: z.int().positive(),
    framesPerSecond: z.literal(30), durationSeconds: z.number().positive(), totalFrames: z.int().positive() }).strict(),
  selection: z.object({ inFrame: z.int().nonnegative(), outFrame: z.int().nonnegative(),
    frameConvention: z.literal('zero-based, both endpoints inclusive'), timelineDurationSeconds: z.number().positive(),
    inSeconds: z.number().nonnegative(), outSeconds: z.number().nonnegative(),
    firstRecordedAt: instant.nullable(), lastRecordedAt: instant.nullable() }).strict(),
  settings: z.record(z.string(), MetadataJsonSchema),
}).strict().refine(value => new TextEncoder().encode(JSON.stringify(value)).length <= 60_000, 'Export metadata exceeds its size limit.');
export type ExportMetadata = z.infer<typeof ExportMetadataSchema>;

export interface ExportMetadataInput {
  id: string; createdAt: string; title: string;
  output: Omit<RenderOutput, 'framesPerSecond'> & { framesPerSecond: number }; mapStyle?: string | undefined;
  accountUsername?: string | null | undefined; displayName?: string | null | undefined;
  ride?: Pick<NormalizedRide, 'sourceFileName' | 'points'> | undefined;
  timing?: ReturnType<typeof resolveExportTiming> | undefined;
}

/** Explicitly selects picture settings; account objects and source coordinates are never serialized. */
export function createExportMetadata(input: ExportMetadataInput): ExportMetadata {
  const { output } = input;
  const timing = input.timing ?? resolveExportTiming(output);
  const username = input.accountUsername || null;
  const title = input.title.replace(/\0/gu, '') || 'Trace';
  const settings = JSON.parse(JSON.stringify({
    cameraStyle: output.cameraStyle, cameraStyleVersion: output.cameraStyleVersion,
    cameraDistance: output.cameraDistance, cameraAngle: output.cameraAngle ?? null, cameraGimbal: output.cameraGimbal ?? null,
    cinematicSequence: output.cinematicSequence ?? null, depthOfField: output.depthOfField ?? null,
    mapStyle: input.mapStyle ?? output.mapStyle ?? 'mcnally', lineStyle: output.lineStyle,
    trackAppearance: output.trackAppearance ?? null, presentation: output.presentation ?? null,
    unitSystem: output.unitSystem ?? 'metric', terrainExaggeration: output.terrainExaggeration ?? 1,
    hidePlaces: output.hidePlaces ?? false, placeTitle: output.placeTitle ?? null,
  })) as Record<string, MetadataJson>;
  return ExportMetadataSchema.parse({
    schemaVersion: 1, product: 'OVERLAND TRACES', website: 'https://overlandtraces.com',
    exportId: input.id, createdAt: new Date(input.createdAt).toISOString(), title,
    filename: videoDownloadFilename({ id: input.id, title, preset: output.preset, createdAt: input.createdAt }),
    accountUsername: username, creator: username ?? (input.displayName || null),
    sourceFileName: input.ride?.sourceFileName?.split(/[\\/]/u).at(-1) || null,
    format: { aspect: output.preset.split('-').at(-1), width: output.width, height: output.height,
      framesPerSecond: output.framesPerSecond, durationSeconds: timing.durationSeconds, totalFrames: timing.totalFrames },
    selection: { inFrame: timing.inFrame, outFrame: timing.outFrame,
      frameConvention: 'zero-based, both endpoints inclusive', timelineDurationSeconds: timing.timelineDurationSeconds,
      inSeconds: timing.inFrame / output.framesPerSecond, outSeconds: timing.outFrame / output.framesPerSecond,
      // Trimmed film time can include choreography; do not claim an unverified GPS-time mapping.
      firstRecordedAt: timing.inFrame === 0 ? input.ride?.points[0]?.recordedAt ?? null : null,
      lastRecordedAt: timing.outFrame === timing.filmFrames - 1 ? input.ride?.points.at(-1)?.recordedAt ?? null : null },
    settings,
  });
}

export function readExportMetadata(value: string | null | undefined): ExportMetadata | undefined {
  return value == null ? undefined : ExportMetadataSchema.parse(JSON.parse(value));
}

/** Refuse a saved snapshot when the worker would render different frames or picture settings. */
export function assertExportMetadataMatchesCapture(metadata: ExportMetadata,
  input: Pick<ExportMetadataInput, 'output' | 'ride' | 'mapStyle' | 'timing'>): void {
  const actual = createExportMetadata({ ...input, id: metadata.exportId, createdAt: metadata.createdAt, title: metadata.title });
  if (JSON.stringify(metadata.format) !== JSON.stringify(actual.format)
    || JSON.stringify(metadata.settings) !== JSON.stringify(actual.settings)
    || JSON.stringify(metadata.selection) !== JSON.stringify(actual.selection)) throw new Error('Export metadata and capture settings disagree.');
}

/** Local and single-coordinator queues reserve names synchronously before any await. */
export function uniqueExportMetadata(input: ExportMetadataInput, filenames: Iterable<string | undefined>): ExportMetadata {
  const metadata = createExportMetadata(input);
  metadata.filename = availableVideoDownloadFilename({ id: input.id, title: input.title, preset: input.output.preset,
    createdAt: input.createdAt }, filenames);
  return metadata;
}
